package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.Report
import co.uk.sacursoftware.ssinventory.data.model.ReportDetail
import co.uk.sacursoftware.ssinventory.data.model.GenerateReportRequest
import co.uk.sacursoftware.ssinventory.data.model.formatDecimal
import co.uk.sacursoftware.ssinventory.data.model.formatWhole
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.DateTimeUtils
import co.uk.sacursoftware.ssinventory.util.LocalUserRole
import co.uk.sacursoftware.ssinventory.util.UserRole
import androidx.compose.ui.text.style.TextAlign
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ReportDetailsScreen(
    report: Report,
    onBack: () -> Unit,
) {
    val userRole = LocalUserRole.current
    val scope = rememberCoroutineScope()
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    
    var details by remember { mutableStateOf<List<ReportDetail>>(emptyList()) }
    var supplierNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var itemNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }
    var showDeleteReportDialog by remember { mutableStateOf(false) }

    LaunchedEffect(report.id) {
        isLoading = true
        repository.getReportDetails(report.id).onSuccess { reportDetails ->
            details = reportDetails
            
            // 1. Fetch all suppliers to map names
            repository.getSuppliers().onSuccess { suppliers ->
                val sMap = suppliers.associateBy({ it.id }, { it.name })
                supplierNames = sMap
                
                // 2. Fetch items for each unique supplier in this report to map item names
                val uniqueSupplierIds = reportDetails.map { it.supplierId }.distinct()
                val newItemNames = itemNames.toMutableMap()
                
                uniqueSupplierIds.forEach { sId ->
                    repository.getItems(sId).onSuccess { items ->
                        items.forEach { item ->
                            newItemNames[item.id] = item.name
                        }
                    }
                }
                itemNames = newItemNames
                isLoading = false
            }
        }.onFailure {
            isLoading = false
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            topBar = {
                TopAppBar(
                    title = { 
                        Column {
                            Text("Report Details", style = MaterialTheme.typography.titleMedium)
                            Text("${report.startDate} to ${report.endDate}", style = MaterialTheme.typography.bodySmall)
                        }
                    },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        if (userRole == UserRole.ADMIN) {
                            IconButton(onClick = { showDeleteReportDialog = true }) {
                                Text("DEL", color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.labelSmall)
                            }
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading) {
                    val groupedDetails = details.groupBy { it.supplierId }
                    
                    Column(modifier = Modifier.fillMaxSize()) {
                        LazyColumn(modifier = Modifier.weight(1f)) {
                            groupedDetails.forEach { (supplierId, items) ->
                                item {
                                    SupplierHeader(supplierNames[supplierId] ?: "Supplier #$supplierId")
                                }
                                
                                item {
                                    ReportTableHeader()
                                }
                                
                                items(items) { item ->
                                    ReportItemRow(item, itemNames[item.itemId] ?: item.itemName ?: "Item #${item.itemId}")
                                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                                }
                            }
                        }
                        
                        // Grand Total Footer
                        if (userRole == UserRole.ADMIN) {
                            Surface(
                                color = MaterialTheme.colorScheme.primaryContainer,
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Text("TOTAL SPENT", fontWeight = FontWeight.Bold)
                                    Text("£${formatDecimal(report.totalSpent)}", fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
                                }
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }

        if (showDeleteReportDialog) {
            ConfirmDeleteDialog(
                message = "Delete this report? This cannot be undone.",
                onConfirm = {
                    showDeleteReportDialog = false
                    scope.launch {
                        repository.deleteReport(report.id).onSuccess {
                            onBack()
                        }
                    }
                },
                onDismiss = { showDeleteReportDialog = false }
            )
        }
    }
}

@Composable
fun SupplierHeader(name: String) {
    Surface(
        color = MaterialTheme.colorScheme.secondaryContainer,
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = name,
            modifier = Modifier.padding(12.dp),
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSecondaryContainer
        )
    }
}

@Composable
fun ReportTableHeader() {
    Row(
        modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary).padding(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text("Item Name", modifier = Modifier.weight(2f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodySmall)
        Text("Opening", modifier = Modifier.weight(0.8f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text("Deliv", modifier = Modifier.weight(0.8f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text("Closing", modifier = Modifier.weight(0.8f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text("Usage", modifier = Modifier.weight(0.8f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text("Spent", modifier = Modifier.weight(0.9f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
    }
}

@Composable
fun ReportItemRow(detail: ReportDetail, itemName: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(text = itemName, modifier = Modifier.weight(2f), style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold)
        Text(formatWhole(detail.openingStock), modifier = Modifier.weight(0.8f), textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text(formatWhole(detail.deliveredStock), modifier = Modifier.weight(0.8f), textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text(formatWhole(detail.closingStock), modifier = Modifier.weight(0.8f), textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall)
        Text(formatWhole(detail.usageAmount), modifier = Modifier.weight(0.8f), textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.secondary, fontWeight = FontWeight.Bold)
        Text("£${formatDecimal(detail.itemSpent)}", modifier = Modifier.weight(0.9f), textAlign = TextAlign.Center, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = MaterialTheme.colorScheme.primary)
    }
}
